import React, { useCallback, useState } from 'react';
import { updateWorkplaceById } from 'core/API/workPlace';
import { createWorkingHour, updateWorkingHour, deleteWorkingHour } from 'core/API/workingHours';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import styles from './EditWorkplaceSettings.module.scss';
import { useFormik } from 'formik';
import TextField from '@mui/material/TextField';
import { Button } from '../../../../../../components/ui/button';
import { Button as MuiButton } from '@mui/material';
import WorkingHoursPicker from '../../../../../../core/components/WorkingHoursPicker';
import { Autocomplete, Checkbox, FormControlLabel, Grid, CircularProgress } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { GetTimeZones } from '../../../../../../core/API/workplaces';
import { getDays } from '../../../../../../core/API/organizationSettings';
import { COUNTRY_LIST, COUNTRY_STATES } from '../../constants';
import dayjs from 'dayjs';
import * as Yup from 'yup';
// settingsUtils not needed here after postal code change
import settingsUtils from '../../utils';
import { TimeZone, Workplace } from 'types';
import { Days } from 'types/Organization';

interface WorkplaceRequest {
	name: string;
	streetAddress: string;
	city: string;
	postcode: string;
	state: string;
	country: string;
	timeZone: string;
	workplaceUnitNumber: string;
	startOfWeek?: string;
	closed: boolean;
	archived: boolean;
	workplaceWorkingHours: any[];
	openDate?: string | null;
	closedDate?: string | null;
}

interface EditWorkplaceSettingProps {
	workplace: Workplace;
	handleBackClick?: () => void;
}

const EditWorkplaceSettings: React.FC<EditWorkplaceSettingProps> = ({
	workplace,
	handleBackClick,
}) => {
	// Removed unused state
	const [apiError, setApiError] = useState<string | null>(null);
	const [apiSuccess, setApiSuccess] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [timeZones, setTimeZones] = useState<TimeZone[]>([]);
	const [days, setDays] = useState<Days[]>([]);
	const [initialWorkingHours] = useState(workplace.workplaceWorkingHours || []);
	const { showToast } = useToastContext();

	const validationSchema = Yup.object().shape({
		// Required fields
		unit_number: Yup.string()
			.trim()
			.required('Workplace Unit Number is required.')
			.matches(/^[0-9]+$/, 'Workplace Unit Number must contain only digits.')
				.min(1, 'Workplace Unit Number must be at least 1 character.'),
			name: Yup.string().trim().required('Name is required.').min(1, 'Name cannot be empty.'),
		streetAddress: Yup.string()
			.trim()
			.required('Street Address is required.')
			.min(1, 'Street Address cannot be empty.'),
		city: Yup.string().trim().required('City is required.').min(1, 'City cannot be empty'),
		state: Yup.string().trim().required('State is required.'),
		postcode: Yup.string()
			.trim()
			.required('Postal code is required.')
			.test('is-valid-postal-code', 'Invalid postal code format.', function (value) {
				const country = (this.parent as any)?.country;
				return !value ? false : settingsUtils.isValidPostalCode(value, country);
			}),
		country: Yup.string().trim().required('Country is required.'),

		// Required fields
		timeZone: Yup.string()
			.trim()
			.required('Time Zone is required.')
			.max(100, 'Time Zone must be less than 100 characters.'),

		// Boolean fields (no validation needed as they're controlled)
		closed: Yup.boolean(),

		// Optional string fields
		// first_day_of_the_week removed
		// Arrays
		working_hours: Yup.array()
			.test(
				'has-open-day',
				'At least one working day is required unless workplace is marked as closed.',
				function (value) {
					const { closed } = this.parent as any;
					if (closed) return true;
					if (!Array.isArray(value)) return false;
					return value.some((wh) => wh && !wh.closed);
				}
			)
			.test(
				'complete-working-hours',
				'All selected working days must have both start and end times.',
				function (value) {
					// const { closed } = this.parent as any;
					// if (closed) return true;
					if (!Array.isArray(value)) return false;
					
					// Check each working hour entry
					for (const wh of value) {
						if (wh && !wh.closed) {
							// If day is enabled (not closed), both openAt and closeAt are required
							if (!wh.openAt || !wh.closeAt) {
								return this.createError({
									message: 'All selected working days must have both start and end times.'
								});
							}
							
						}
					}
					return true;
				}
			),
		approved_forms: Yup.array(),
		// Date fields
		closedDate: Yup.date()
			.nullable()
			.when('closed', {
				is: true,
				then: (schema) =>
					schema.required('Closed date is required when workplace is marked as closed'),
				otherwise: (schema) => schema.nullable(),
			}),
		openDate: Yup.date().nullable().required('Open date is required'),
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			id: workplace.id || '',
			name: workplace.name || '',
			streetAddress: workplace.streetAddress || '',
			city: workplace.city || '',
			state: workplace.state || '',
			postcode: workplace.postcode || '',
			country: workplace.country || '',
			timeZone: workplace.timeZone || '',
			unit_number: workplace.workplaceUnitNumber || '',
			working_hours: workplace.workplaceWorkingHours || [],
			// first_day_of_the_week removed
			closed: workplace.closed || false,
			closedDate: workplace.closedDate ? dayjs(workplace.closedDate) : null,
			openDate: workplace.openDate ? dayjs(workplace.openDate) : null,
		},
		validationSchema,
		onSubmit: async (values, { setSubmitting, setFieldValue }) => {
			try {
				setApiError(null);
				setApiSuccess(null);
				setIsSubmitting(true);

				// Ensure unit number is padded with leading zeros to at least 4 digits for saving
				const rawUnitNumber = String(values.unit_number || '').trim();
				const digitsOnly = rawUnitNumber.replace(/\D/g, '');
				const paddedUnitNumber = digitsOnly.padStart(4, '0');
				// Update form value so UI reflects the saved format
				setFieldValue('unit_number', paddedUnitNumber);

				// Prepare the payload with exact keys as per the API requirements
				const payload: WorkplaceRequest = {
					name: values.name,
					streetAddress: values.streetAddress,
					city: values.city,
					postcode: values.postcode,
					state: values.state,
					country: values.country,
					timeZone: `${values.timeZone}`,
					workplaceUnitNumber: paddedUnitNumber,
					closed: values.closed,
					archived: false,
					workplaceWorkingHours: values.working_hours,
					openDate: values.openDate ? values.openDate.format('YYYY-MM-DD') : null,
					closedDate: values.closedDate ? values.closedDate.format('YYYY-MM-DD') : null,
				};

				// Update the workplace first
				await updateWorkplaceById(workplace.id, payload);

				// Handle working hours deletions for days that were changed to closed
				const deletionPromises = initialWorkingHours
					.filter((initialWh: any) => {
						// Find the corresponding working hour in current values
						const currentWh = values.working_hours.find(
							(wh: any) => wh['@id'] === initialWh['@id']
						);
						// Delete if it was open initially but is now closed
						return initialWh['@id'] && !initialWh.closed && currentWh?.closed;
					})
					.map(async (wh: any) => {
						const whId = wh['@id'].split('/').pop();
						await deleteWorkingHour(whId);
					});

				await Promise.all(deletionPromises || []);

				// Handle working hours updates
				const workingHoursPromises = values.working_hours
					?.filter((wh) => !wh.closed)
					.map(async (wh: any) => {
						const workingHourPayload = {
							workplace: `/api/workplaces/${workplace.id}`,
							day: wh.day,
							openAt: wh.openAt,
							closeAt: wh.closeAt,
						};
						// console.log('workingHourPayload', wh);
						if (wh['@id']) {
							// If the working hour has an ID, update it
							// Extract the ID from the '@id' field
							const whIdParts = wh['@id'].split('/');
							const whId = whIdParts[whIdParts.length - 1];
							await updateWorkingHour(whId, workingHourPayload);
						} else {
							// If it's a new working hour, create it
							await createWorkingHour(workingHourPayload);
						}
					});

				await Promise.all(workingHoursPromises || []);

				setApiSuccess('Workplace updated successfully!');
				showToast('success', 'Workplace updated successfully!');
				// console.log('Workplace Response:', workplaceResponse);
				handleBackClick();

				// Optionally call handleBackClick after successful update
				// setTimeout(() => handleBackClick?.(), 2000);
			} catch (error: any) {
				console.log('error', error);
				const errorMessage = error?.message || 'Failed to update workplace';
				setApiError(errorMessage);
				showToast('error', errorMessage);
				console.error('Update error:', error);
			} finally {
				setIsSubmitting(false);
				setSubmitting(false);
			}
		},
	});
	// console.log(
	// 	'timezine',
	// 	timeZones,
	// 	formik.values,
	// 	timeZones.find((tz) => tz?.['@id'] === formik.values.timeZone)
	// );

	// Fetch time zones, days, and working hours on component mount
	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const [zones, daysData] = await Promise.all([
					GetTimeZones(),
					getDays(),
					// getWorkingHours(workplace.id || workplace.intId)
				]);
				setTimeZones(zones || []);
				setDays(daysData || []);
			} catch (error) {
				console.error('Failed to fetch data:', error);
			}
		};
		fetchData();
	}, [workplace.id, workplace.intId]);

	// start_of_week removed

	const handleSubmit = () => {
		// console.log('values', formik.values);
		formik.handleSubmit();
	};

	const handleWorkingHoursChange = useCallback(
		(value: any[]) => {
			console.log('working hours changed', value, formik.errors);
			const formattedWorkingHours = value.map((dayConfig) => ({
				'@id': dayConfig?.['@id'],
				day: dayConfig.day['@id'],
				closed: !dayConfig.enabled,
				openAt: dayConfig.startTime ? dayConfig.startTime.format('HH:mm:ss') : null,
				closeAt: dayConfig.endTime ? dayConfig.endTime.format('HH:mm:ss') : null,
			}));

			formik.setFieldValue('working_hours', formattedWorkingHours);
		},
		[formik]
	);

	if (!workplace) {
		return <div className={styles.loading}><CircularProgress size={20} color="inherit" /></div>;
	}

	return (
		<div className={styles.editWorkplaceSetting}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</Grid>
					<Grid>
						<Button className={styles.backButton} onClick={handleSubmit} disabled={isSubmitting}>
							{isSubmitting ? 'Saving...' : 'Save'}
						</Button>
					</Grid>
				</Grid>
				<Grid xs={12} direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>Workplace Details</h2>
				</Grid>
			</Grid>
			<h3 className={styles.title}>{workplace.name}</h3>
			{/* API Feedback Messages */}
			{/* {apiSuccess && (
				<div
					style={{
						color: 'green',
						marginTop: '16px',
						padding: '8px',
						backgroundColor: '#f0f8ff',
						borderRadius: '4px',
					}}
				>
					{apiSuccess}
				</div>
			)} */}
			{/* {apiError && (
				<div
					style={{
						color: 'red',
						marginTop: '16px',
						padding: '8px',
						backgroundColor: '#ffe6e6',
						borderRadius: '4px',
					}}
				>
					{apiError}
				</div>
			)} */}
			{formik.touched.working_hours && formik.errors.working_hours && (
				<div
					className="error-message"
					style={{
						color: 'red',
						marginTop: '16px',
						padding: '8px',
						backgroundColor: '#ffe6e6',
						borderRadius: '4px',
					}}
				>
					{formik.errors.working_hours as string}
				</div>
			)}
			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{/* Workplace Unit Number */}
				<TextField
					label="Workplace Unit Number"
					name="unit_number"
					required
					value={formik.values.unit_number}
					onChange={(e) => {
						const digits = String(e.target.value || '').replace(/\D/g, '');
						formik.setFieldValue('unit_number', digits);
					}}
					inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
					error={formik.touched.unit_number && Boolean(formik.errors.unit_number)}
					helperText={formik.touched.unit_number && (formik.errors.unit_number as string)}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				{/* Name */}
				<TextField
					label="Name"
					name="name"
					required
					value={formik.values.name}
					onChange={formik.handleChange}
					error={formik.touched.name && Boolean(formik.errors.name)}
					helperText={formik.touched.name && (formik.errors.name as string)}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				{/* Address */}
				<TextField
					label="Street Address"
					name="streetAddress"
					required
					value={formik.values.streetAddress}
					onChange={formik.handleChange}
					error={formik.touched.streetAddress && Boolean(formik.errors.streetAddress)}
					helperText={formik.touched.streetAddress && (formik.errors.streetAddress as string)}
					className={styles.input}
					margin="normal"
					fullWidth
				/>
				<TextField
					label="City"
					name="city"
					required
					value={formik.values.city}
					onChange={formik.handleChange}
					error={formik.touched.city && Boolean(formik.errors.city)}
					helperText={formik.touched.city && (formik.errors.city as string)}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				<Autocomplete
					options={COUNTRY_STATES[formik.values.country] || []}
					getOptionLabel={(option) => option}
					value={formik.values.state || ''}
					onChange={(_, value) => formik.setFieldValue('state', value || '')}
					renderInput={(params) => (
						<TextField
							{...params}
							label="State"
							required={true}
							error={formik.touched.state && Boolean(formik.errors.state)}
							helperText={formik.touched.state && (formik.errors.state as string)}
							className={styles.input}
							margin="normal"
							fullWidth
						/>
					)}
				/>

				<TextField
					label="Postal Code"
					name="postcode"
					required
					value={formik.values.postcode}
					onChange={formik.handleChange}
					error={formik.touched.postcode && Boolean(formik.errors.postcode)}
					helperText={formik.touched.postcode && (formik.errors.postcode as string)}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				<Autocomplete
					options={COUNTRY_LIST}
					getOptionLabel={(option) => option}
					value={formik.values.country || ''}
					onChange={(_, value) => {
						const newCountry = value || '';
						formik.setFieldValue('country', newCountry);
						// If a timezone is selected but its country doesn't match the new country, clear it
						const selectedTz = timeZones.find((tz) => tz['@id'] === formik.values.timeZone);
						if (selectedTz && selectedTz.country) {
							if (selectedTz.country.toString().toLowerCase() !== newCountry.toString().toLowerCase()) {
								formik.setFieldValue('timeZone', '');
							}
						}
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							required={true}
							label="Country"
							error={formik.touched.country && Boolean(formik.errors.country)}
							helperText={formik.touched.country && (formik.errors.country as string)}
							className={styles.input}
							margin="normal"
							fullWidth
						/>
					)}
				/>

				{/* Time Zone */}
				<Autocomplete
					options={
						formik.values.country
							? timeZones.filter((tz) => {
								  const country = (tz?.country || (tz as any)?.country_name || '').toString();
								  return (
									  !country ||
									  country.toLowerCase() === formik.values.country.toString().toLowerCase()
								  );
							  })
							: timeZones
					}
					getOptionLabel={(option: TimeZone) => option.label}
					value={timeZones?.find((tz) => tz['@id'] === formik.values.timeZone) || null}
					onChange={(_, value) => {
						const timeZoneValue = value?.['@id'] || '';
						formik.setFieldValue('timeZone', timeZoneValue);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Time Zone"
							required
							error={formik.touched.timeZone && Boolean(formik.errors.timeZone)}
							helperText={formik.touched.timeZone && (formik.errors.timeZone as string)}
							className={styles.input}
							margin="normal"
							fullWidth
						/>
					)}
				/>

				{/* Start of Week removed */}

				{/* Open Date */}
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker
						label="Open Date"
						value={formik.values.openDate}
						onChange={(value) => formik.setFieldValue('openDate', value)}
						format="MM/DD/YYYY"
						slotProps={{
							textField: {
								required: true,
								error: formik.touched.openDate && Boolean(formik.errors.openDate),
								helperText: formik.touched.openDate && (formik.errors.openDate as string),
								fullWidth: true,
								margin: 'normal',
								className: styles.input,
							},
						}}
					/>
				</LocalizationProvider>

				{/* Working Hours */}
				<div className={styles.workingHoursRow}>
					<WorkingHoursPicker
						value={formik.values.working_hours}
						onChange={handleWorkingHoursChange}
						days={days}
						validateOnChange={true}
						// onChange={val => formik.setFieldValue('working_hours', val)}
					/>
				</div>
				<Grid container direction="row" justifyContent="flex-start" alignItems="center" xs={12}>
					<Grid xs={3}>
						<FormControlLabel
							control={
								<Checkbox
									checked={formik.values.closed}
									onChange={(e) => {
										formik.setFieldValue('closed', e.target.checked);
										// Clear closedDate if workplace is not closed
										if (!e.target.checked) {
											formik.setFieldValue('closedDate', null);
										}
									}}
								/>
							}
							label={'Closed'}
						/>
					</Grid>
					<Grid xs={9} justifyContent="flex-start" alignItems="center">
						{formik.values.closed && (
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DatePicker
									label="Closed Date"
									value={formik.values.closedDate}
									onChange={(value) => formik.setFieldValue('closedDate', value)}
									format="MM/DD/YYYY"
									slotProps={{
										textField: {
											error: formik.touched.closedDate && Boolean(formik.errors.closedDate),
											helperText: formik.touched.closedDate && (formik.errors.closedDate as string),
											fullWidth: true,
											margin: 'normal',
											className: styles.input,
										},
									}}
								/>
							</LocalizationProvider>
						)}
					</Grid>
				</Grid>
			</form>
		</div>
	);
};

export default EditWorkplaceSettings;
